import React, { useEffect } from "react";
import { ThemeProvider } from "styled-components";
import { useApp } from "./contexts/AppContext";
import themeDefault from "theme/default";
import PropTypes from "prop-types";

const WhitelabelThemeProvider = ({ children }) => {
  const { whitelabel } = useApp();

  useEffect(() => {
    const name =
      typeof whitelabel?.assets?.name === "string"
        ? whitelabel.assets.name.trim()
        : "";
    if (name) {
      document.title = name;
    }

    const faviconUrl =
      typeof whitelabel?.assets?.favicon_url === "string"
        ? whitelabel.assets.favicon_url.trim()
        : "";

    const existing =
      document.querySelector('link[rel="icon"]') ||
      document.querySelector('link[rel="shortcut icon"]') ||
      document.querySelector('link[rel~="icon"]');
    const link =
      existing || Object.assign(document.createElement("link"), { rel: "icon" });

    link.href = faviconUrl || "data:,";

    if (!existing) document.head.appendChild(link);
  }, [whitelabel]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const gtmRaw =
      typeof whitelabel?.analytics?.gtm_container_id === "string"
        ? whitelabel.analytics.gtm_container_id.trim()
        : "";

    // Support multiple GTM IDs separated by comma, semicolon, or whitespace.
    const gtmIds = gtmRaw
      .split(/[,;\s]+/)
      .map((id) => id.trim())
      .filter(Boolean);

    const GTM_SCRIPT_CLASS = "gtm-dynamic-loader";
    const GTM_NOSCRIPT_CLASS = "gtm-dynamic-noscript";

    // Remove all previously injected GTM elements.
    const removeExisting = () => {
      document.querySelectorAll(`.${GTM_SCRIPT_CLASS}`).forEach((el) => el.remove());
      document.querySelectorAll(`.${GTM_NOSCRIPT_CLASS}`).forEach((el) => el.remove());
    };

    if (gtmIds.length === 0) {
      removeExisting();
      return;
    }

    // Build a comparable key to avoid re-injecting the same set.
    const idsKey = gtmIds.join(",");
    const existingScript = document.querySelector(`.${GTM_SCRIPT_CLASS}`);
    if (existingScript?.dataset?.gtmIds === idsKey) return;

    removeExisting();

    // Inject one GTM snippet per container ID.
    gtmIds.forEach((gtmId) => {
      // Google Tag Manager (script)
      const script = document.createElement("script");
      script.className = GTM_SCRIPT_CLASS;
      script.dataset.gtmIds = idsKey;
      script.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
      document.head.appendChild(script);

      // Google Tag Manager (noscript)
      const noscript = document.createElement("noscript");
      noscript.className = GTM_NOSCRIPT_CLASS;
      const iframe = document.createElement("iframe");
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}`;
      iframe.height = "0";
      iframe.width = "0";
      iframe.style.display = "none";
      iframe.style.visibility = "hidden";
      noscript.appendChild(iframe);

      if (document.body.firstChild) {
        document.body.insertBefore(noscript, document.body.firstChild);
      } else {
        document.body.appendChild(noscript);
      }
    });
  }, [whitelabel]);

  const theme = {
    ...themeDefault,
    whitelabel,
  };

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

WhitelabelThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default WhitelabelThemeProvider;
